import type { ID } from "@/lib/catalog/types";
import { api } from "@/lib/apiClient";

export interface CategoryAttributeLink {
  id: ID;
  categoryId: ID;
  attributeId: ID;
  sortOrder: number;
}

const LS_KEY = "inventory.categoryAttributeLinks.v1";

type CategoryAttributeLinkApiRecord = {
  id: number | string;
  category_id: number | string;
  attribute_id: string;
  sort_order: number;
};

type ListCategoryAttributeLinksApiResponse = {
  data?: CategoryAttributeLinkApiRecord[];
};

type UpsertCategoryAttributeLinksApiResponse = {
  message?: string;
  data?: CategoryAttributeLinkApiRecord[];
};

function dedupeById(items: CategoryAttributeLink[]): CategoryAttributeLink[] {
  const byId = new Map<ID, CategoryAttributeLink>();
  for (const item of items) {
    byId.set(item.id, item);
  }
  return Array.from(byId.values());
}

function load(): CategoryAttributeLink[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as CategoryAttributeLink[];
    return Array.isArray(parsed) ? dedupeById(parsed) : [];
  } catch {
    return [];
  }
}

function save(items: CategoryAttributeLink[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupeById(items)));
}

export function listLinksForCategory(categoryId: ID): CategoryAttributeLink[] {
  return load()
    .filter((l) => l.categoryId === categoryId)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

export function listAllLinks(): CategoryAttributeLink[] {
  return load();
}

export function setCategoryAttributeLinks(categoryId: ID, orderedAttributeIds: ID[]) {
  const rest = load().filter((l) => l.categoryId !== categoryId);
  const next: CategoryAttributeLink[] = orderedAttributeIds.map((attributeId, idx) => ({
    id: crypto.randomUUID(),
    categoryId,
    attributeId,
    sortOrder: idx,
  }));
  save([...rest, ...next]);
}

function mapApiLink(link: CategoryAttributeLinkApiRecord): CategoryAttributeLink {
  return {
    id: String(link.id),
    categoryId: String(link.category_id),
    attributeId: String(link.attribute_id),
    sortOrder: link.sort_order,
  };
}

export async function syncLinksForCategoryFromApi(categoryId: ID) {
  const response = await api.get<ListCategoryAttributeLinksApiResponse>(`/api/v1/catalog/categories/${categoryId}/attribute-links`);
  const links = (response.data?.data ?? []).map(mapApiLink);
  const rest = load().filter((l) => l.categoryId !== categoryId);
  save([...rest, ...links]);
  return listLinksForCategory(categoryId);
}

export async function setCategoryAttributeLinksViaApi(categoryId: ID, orderedAttributeIds: ID[]) {
  const response = await api.put<UpsertCategoryAttributeLinksApiResponse>(
    `/api/v1/catalog/categories/${categoryId}/attribute-links`,
    { attribute_ids: orderedAttributeIds },
  );
  const links = (response.data?.data ?? []).map(mapApiLink);
  const rest = load().filter((l) => l.categoryId !== categoryId);
  save([...rest, ...links]);
  return {
    links: listLinksForCategory(categoryId),
    message: response.data?.message || "Category attribute links updated successfully.",
  };
}

/** Fetches current links, appends `attributePublicId` if missing, then saves the full list (replaces on server). */
export async function appendAttributeIdToCategory(
  categoryId: ID,
  attributePublicId: ID,
): Promise<{
  links: CategoryAttributeLink[];
  message: string;
  alreadyLinked: boolean;
}> {
  const current = await syncLinksForCategoryFromApi(categoryId);
  const orderedIds = current.map((l) => l.attributeId);
  if (orderedIds.includes(attributePublicId)) {
    return {
      links: listLinksForCategory(categoryId),
      message: "Attribute is already linked to this category.",
      alreadyLinked: true,
    };
  }
  const result = await setCategoryAttributeLinksViaApi(categoryId, [...orderedIds, attributePublicId]);
  return {
    links: result.links,
    message: result.message,
    alreadyLinked: false,
  };
}

export function removeLinksForAttribute(attributeId: ID) {
  save(load().filter((l) => l.attributeId !== attributeId));
}

export function removeLinksForCategory(categoryId: ID) {
  save(load().filter((l) => l.categoryId !== categoryId));
}

export function replaceAllLinks(links: CategoryAttributeLink[]) {
  save(links);
}
